/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Unified API Client for NextGen Lakshya platform.
 * Validates HTTP response status and strictly checks Content-Type header
 * to prevent HTML fallback pages (index.html) from being incorrectly parsed as JSON.
 */

import initialDbData from '../../data/db.json';

export interface ApiClientOptions extends RequestInit {
  token?: string | null;
}

export class ApiError extends Error {
  status: number;
  data: any;
  contentType: string;

  constructor(message: string, status: number, data?: any, contentType: string = '') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.contentType = contentType;
  }
}

/**
 * Fallback dataset resolver for static hosting environments (e.g. Firebase Hosting)
 */
export function getEndpointFallbackData(endpoint: string): any | null {
  const cleanEndpoint = endpoint.split('?')[0];

  let dbState: any = null;
  const cachedStr = typeof localStorage !== 'undefined' ? localStorage.getItem('cached_db_state') : null;
  if (cachedStr) {
    try { dbState = JSON.parse(cachedStr); } catch (e) {}
  }
  if (!dbState) {
    dbState = initialDbData;
  }

  if (cleanEndpoint === '/api/public/cms' || cleanEndpoint === '/api/admin/cms') {
    return dbState.cms || initialDbData.cms;
  }
  if (cleanEndpoint === '/api/public/notices' || cleanEndpoint === '/api/admin/notices') {
    return dbState.noticeBoard || initialDbData.noticeBoard;
  }
  if (cleanEndpoint === '/api/public/blogs' || cleanEndpoint === '/api/admin/blogs') {
    return dbState.blogPosts || initialDbData.blogPosts;
  }
  if (cleanEndpoint === '/api/public/legal' || cleanEndpoint === '/api/admin/legal') {
    return dbState.legalPages || initialDbData.legalPages;
  }
  if (cleanEndpoint === '/api/public/institutes' || cleanEndpoint === '/api/admin/institutes') {
    return dbState.institutes || initialDbData.institutes;
  }
  if (cleanEndpoint === '/api/public/plans' || cleanEndpoint === '/api/admin/plans') {
    return dbState.plans || initialDbData.plans;
  }
  if (cleanEndpoint === '/api/student/tests' || cleanEndpoint === '/api/admin/tests') {
    return dbState.tests || initialDbData.tests;
  }
  if (cleanEndpoint === '/api/admin/question-bank') {
    return dbState.questionBank || initialDbData.questionBank;
  }
  if (cleanEndpoint === '/api/competition/public-blogs' || cleanEndpoint === '/api/admin/competition/submissions') {
    return dbState.competitionBlogs || initialDbData.competitionBlogs;
  }
  if (cleanEndpoint === '/api/competition/status' || cleanEndpoint === '/api/admin/competition/registrations') {
    return dbState.competitionParticipants || initialDbData.competitionParticipants;
  }
  if (cleanEndpoint === '/api/admin/competition/votes') {
    return dbState.competitionVotes || initialDbData.competitionVotes;
  }
  if (cleanEndpoint === '/api/admin/users' || cleanEndpoint === '/api/admin/admins') {
    return dbState.users || initialDbData.users;
  }
  if (cleanEndpoint === '/api/student/notifications') {
    return dbState.globalNotifications || initialDbData.globalNotifications;
  }
  if (cleanEndpoint === '/api/admin/promo-codes') {
    return dbState.promoCodes || initialDbData.promoCodes;
  }
  if (cleanEndpoint === '/api/admin/audit-logs') {
    return dbState.adminAuditLogs || initialDbData.adminAuditLogs;
  }
  if (cleanEndpoint === '/api/public/server-time') {
    return { serverTime: Date.now() };
  }
  if (cleanEndpoint.startsWith('/api/student/attempts/') && cleanEndpoint.endsWith('/questions')) {
    const allQuestions = dbState.questionBank || initialDbData.questionBank || [];
    return allQuestions;
  }

  return null;
}

// Global fetch interceptor for static hosting fallbacks
if (typeof window !== 'undefined' && !(window as any).__api_fetch_patched__) {
  (window as any).__api_fetch_patched__ = true;
  const originalFetch = window.fetch ? window.fetch.bind(window) : null;
  if (originalFetch) {
    const customFetch = async function (input: RequestInfo | URL, init?: RequestInit) {
      const urlStr = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
      
      try {
        const response = await originalFetch(input, init);
        const contentType = response.headers.get('content-type') || '';
        
        if (urlStr.includes('/api/') && (!response.ok || contentType.includes('text/html'))) {
          const fallback = getEndpointFallbackData(urlStr);
          if (fallback !== null) {
            return new Response(JSON.stringify(fallback), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        }
        return response;
      } catch (err) {
        if (urlStr.includes('/api/')) {
          const fallback = getEndpointFallbackData(urlStr);
          if (fallback !== null) {
            return new Response(JSON.stringify(fallback), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            });
          }
        }
        throw err;
      }
    };

    try {
      Object.defineProperty(window, 'fetch', {
        value: customFetch,
        writable: true,
        configurable: true,
      });
    } catch (e) {
      try {
        (window as any).fetch = customFetch;
      } catch (err2) {
        console.warn('[apiClient] Unable to intercept global fetch:', err2);
      }
    }
  }
}

/**
 * Unified apiClient fetch wrapper.
 * Validates response status AND Content-Type: application/json header.
 * Throws a descriptive ApiError if HTML or non-JSON content is returned.
 */
export async function apiClient<T = any>(
  endpoint: string,
  options: ApiClientOptions = {}
): Promise<T> {
  const { token, headers: customHeaders, ...fetchOptions } = options;

  // Resolve base URL for API calls if configured or running on static hosts
  let targetUrl = endpoint;
  const apiBaseUrl = (import.meta as any).env?.VITE_API_BASE_URL || 
                     (window as any).__API_BASE_URL__ || 
                     (typeof localStorage !== 'undefined' ? localStorage.getItem('VITE_API_BASE_URL') : null);

  if (apiBaseUrl && endpoint.startsWith('/api/')) {
    const cleanBase = apiBaseUrl.replace(/\/+$/, '');
    targetUrl = `${cleanBase}${endpoint}`;
  }

  const headers: Record<string, string> = {
    'Accept': 'application/json',
    ...(customHeaders as Record<string, string> || {})
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Ensure JSON body content-type if body is a string
  if (fetchOptions.body && typeof fetchOptions.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(targetUrl, {
      ...fetchOptions,
      headers
    });
  } catch (netErr: any) {
    const fallback = getEndpointFallbackData(endpoint);
    if (fallback !== null) {
      return fallback as T;
    }
    throw new ApiError(
      `Network error communicating with server: ${netErr?.message || 'Connection failed'}`,
      0
    );
  }

  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');

  const rawText = await response.text();

  if (!isJson) {
    const fallback = getEndpointFallbackData(endpoint);
    if (fallback !== null) {
      return fallback as T;
    }

    const preview = rawText.trim().substring(0, 100);
    const isHtml = preview.toLowerCase().startsWith('<!doctype') || preview.toLowerCase().startsWith('<html');
    
    throw new ApiError(
      isHtml
        ? `Server endpoint returned HTML page instead of JSON for ${endpoint}. Check if API route exists.`
        : `Server endpoint returned invalid Content-Type "${contentType}" for ${endpoint}.`,
      response.status,
      { rawResponse: rawText },
      contentType
    );
  }

  let data: any = null;
  if (rawText && rawText.trim().length > 0) {
    try {
      data = JSON.parse(rawText);
    } catch (parseErr: any) {
      const fallback = getEndpointFallbackData(endpoint);
      if (fallback !== null) {
        return fallback as T;
      }
      throw new ApiError(
        `Failed to parse JSON response from server for ${endpoint}`,
        response.status,
        { rawResponse: rawText },
        contentType
      );
    }
  } else {
    data = {};
  }

  if (!response.ok) {
    const fallback = getEndpointFallbackData(endpoint);
    if (fallback !== null) {
      return fallback as T;
    }
    const errorMsg = data?.error || data?.message || `API request failed with HTTP ${response.status}`;
    throw new ApiError(errorMsg, response.status, data, contentType);
  }

  return data as T;
}

export interface ApiFetchResult<T = any> {
  ok: boolean;
  status: number;
  data?: T;
  error?: string;
}

export async function safeApiFetch<T = any>(
  url: string,
  options?: RequestInit
): Promise<ApiFetchResult<T>> {
  try {
    const data = await apiClient<T>(url, options);
    return {
      ok: true,
      status: 200,
      data
    };
  } catch (err: any) {
    if (err instanceof ApiError) {
      return {
        ok: false,
        status: err.status,
        data: err.data,
        error: err.message
      };
    }
    return {
      ok: false,
      status: 0,
      error: err?.message || 'Request failed'
    };
  }
}

export default apiClient;

